/**
 * Panel lateral que se abre al hacer click en una casa de la grilla
 * o tabla. Muestra datos clave + acciones: "Ver pagos", "Aplicar saldo a
 * favor" (si hay crédito) y "Suspender servicios" (solo si atrasada).
 *
 * El saldo destacado usa `saldo_a_hoy` (neto MTD), misma cifra y convención
 * que la columna "$ Saldo a hoy" de la grilla — sin divergencia entre vistas.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  aplicarSaldoAFavor,
  type AplicacionSaldoAFavorResponse,
  type CasaAnualResumen,
} from '../../lib/api/casas-tesoreria';
import { formatFecha } from '../../lib/format';
import { Monto, Num } from './Monto';

// Sprint 17 S17-Z5: 3 colores de "al día" + "atrasada" para clasificar
// casas según estado de pago del semestre actual.
const ESTADO_LABEL = {
  al_dia: 'Al día',
  al_dia_pendiente_semestre: 'Al día (con saldo)',
  atrasada: 'Atrasada',
  suspendida: 'Suspendida',
  compensacion: 'Compensación',
} as const;

export interface PanelCasaProps {
  casa: CasaAnualResumen | null;
  onClose: () => void;
  onSuspender?: (casa: CasaAnualResumen) => void;
  // Pieza B: callback para recargar la grilla tras aplicar saldo a favor.
  onAplicado?: () => void;
}

type PasoAplicacion =
  | 'cerrado'
  | 'confirmar'
  | 'aplicando'
  | 'resultado'
  | 'error';

export function PanelCasa({
  casa,
  onClose,
  onSuspender,
  onAplicado,
}: PanelCasaProps) {
  const [paso, setPaso] = useState<PasoAplicacion>('cerrado');
  const [resultado, setResultado] =
    useState<AplicacionSaldoAFavorResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  if (!casa) return null;

  // Sprint 17 S17-Z5: solo se suspende cuando hay atraso real (meses
  // vencidos sin pagar), no por saldo del semestre pendiente.
  const puedeSuspender =
    casa.estado === 'atrasada' && casa.saldo_semestre_actual > 0;
  const tieneSaldoAFavor = casa.saldo_a_favor > 0;

  const debe = casa.saldo_a_hoy > 0;
  const favor = casa.saldo_a_hoy < 0;

  async function confirmarAplicacion() {
    if (!casa) return;
    setPaso('aplicando');
    try {
      const r = await aplicarSaldoAFavor(casa.lote_id);
      setResultado(r);
      setPaso('resultado');
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Error al aplicar el saldo');
      setPaso('error');
    }
  }

  function cerrarResultado() {
    setPaso('cerrado');
    setResultado(null);
    // El saldo de la casa cambió: recargar la grilla y cerrar el panel
    // (el snapshot actual quedó obsoleto).
    onAplicado?.();
    onClose();
  }

  return (
    <>
      {/* Backdrop mobile */}
      <div
        className="md:hidden fixed inset-0 bg-black/60 z-30"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className="fixed md:sticky md:top-0 right-0 md:right-auto top-0 h-screen md:h-auto md:max-h-[calc(100vh-3rem)] w-full md:w-80 bg-bg-card md:bg-bg-sunken border-l border-white/[0.08] z-40 overflow-y-auto">
        <div className="p-4 border-b border-white/[0.08] flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-text-muted">
              Casa
            </div>
            <h3 className="text-lg font-medium text-text-primary mt-0.5">
              <Num>{casa.identificador}</Num>
            </h3>
            <div className="text-xs text-text-secondary mt-1">
              {casa.titular_nombre || 'Sin titular registrado'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text-primary p-1 -mr-1"
            aria-label="Cerrar panel"
          >
            ×
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Saldo destacado — saldo_a_hoy (neto MTD), igual que la grilla. */}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-text-muted">
              Saldo a hoy
            </div>
            <div
              className={`text-2xl font-medium mt-1 ${
                debe
                  ? 'text-red-400'
                  : favor
                    ? 'text-emerald-400'
                    : 'text-text-secondary'
              }`}
            >
              {debe ? (
                <Monto value={casa.saldo_a_hoy} />
              ) : favor ? (
                <>
                  −<Monto value={-casa.saldo_a_hoy} />
                </>
              ) : (
                'Al día'
              )}
            </div>
            <div className="text-[11px] text-text-muted mt-1">
              {debe ? 'Adeuda' : favor ? 'A favor' : 'Sin saldo pendiente'}
            </div>
          </div>

          {/* Estado actual */}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-text-muted">
              Estado
            </div>
            <div className="text-sm text-text-primary mt-1">
              {ESTADO_LABEL[casa.estado]}
            </div>
          </div>

          {/* Último pago */}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-text-muted">
              Último pago
            </div>
            <div className="text-sm text-text-secondary mt-1">
              {casa.ultimo_pago_fecha
                ? formatFecha(casa.ultimo_pago_fecha)
                : 'Sin pagos registrados'}
            </div>
          </div>

          {/* Acciones */}
          <div className="pt-3 border-t border-white/[0.06] space-y-2">
            <Link
              to={`/tesoreria/pagos?lote_id=${casa.lote_id}`}
              className="block text-center text-sm px-3 py-2 rounded border border-white/[0.12] text-text-primary hover:border-white/[0.24]"
            >
              Ver pagos de esta casa →
            </Link>
            {tieneSaldoAFavor && (
              <button
                type="button"
                onClick={() => setPaso('confirmar')}
                className="w-full text-center text-sm px-3 py-2 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"
              >
                Aplicar saldo a favor (<Monto value={casa.saldo_a_favor} />)
              </button>
            )}
            {puedeSuspender && onSuspender && (
              <button
                type="button"
                onClick={() => onSuspender(casa)}
                className="w-full text-center text-sm px-3 py-2 rounded bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20"
              >
                Suspender servicios
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Modal de confirmación / resultado de aplicar saldo a favor */}
      {paso !== 'cerrado' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-sm bg-bg-card border border-white/[0.12] rounded-lg p-5 space-y-4">
            {(paso === 'confirmar' || paso === 'aplicando') && (
              <>
                <h4 className="text-base font-medium text-text-primary">
                  Aplicar saldo a favor
                </h4>
                <p className="text-sm text-text-secondary">
                  Se aplicará el saldo a favor de la casa{' '}
                  <Num>{casa.identificador}</Num> (
                  <Monto value={casa.saldo_a_favor} />) a sus cuotas
                  pendientes, abonando desde la más antigua. No crea pagos
                  nuevos: solo reasigna el crédito ya recibido.
                </p>
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setPaso('cerrado')}
                    disabled={paso === 'aplicando'}
                    className="text-sm px-3 py-2 rounded border border-white/[0.12] text-text-secondary hover:border-white/[0.24] disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={confirmarAplicacion}
                    disabled={paso === 'aplicando'}
                    className="text-sm px-3 py-2 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-50"
                  >
                    {paso === 'aplicando' ? 'Aplicando…' : 'Aplicar'}
                  </button>
                </div>
              </>
            )}

            {paso === 'resultado' && resultado && (
              <>
                <h4 className="text-base font-medium text-text-primary">
                  Saldo a favor aplicado
                </h4>
                <p className="text-sm text-text-secondary">
                  Se aplicaron <Monto value={resultado.total_aplicado} /> a{' '}
                  {resultado.cuotas.length}{' '}
                  {resultado.cuotas.length === 1 ? 'cuota' : 'cuotas'}.
                  {resultado.credito_restante > 0 ? (
                    <>
                      {' '}
                      Crédito restante:{' '}
                      <Monto value={resultado.credito_restante} />.
                    </>
                  ) : resultado.total_aplicado === 0 ? (
                    ' No había cuotas pendientes contra las cuales aplicarlo.'
                  ) : (
                    ' Crédito agotado.'
                  )}
                </p>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={cerrarResultado}
                    className="text-sm px-3 py-2 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25"
                  >
                    Listo
                  </button>
                </div>
              </>
            )}

            {paso === 'error' && (
              <>
                <h4 className="text-base font-medium text-red-400">
                  No se pudo aplicar
                </h4>
                <p className="text-sm text-text-secondary">{errorMsg}</p>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setPaso('cerrado')}
                    className="text-sm px-3 py-2 rounded border border-white/[0.12] text-text-secondary hover:border-white/[0.24]"
                  >
                    Cerrar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
